import { DataTablePrimeReact, Dropzone } from "@finanedi.es/storybook"
import { faSpinnerThird } from "@fortawesome/pro-light-svg-icons"
import { GetDocumentosComercialesImportadosByFiltersDtoProps } from "api/DTOs/GetDocumentosComercialesImportadosByFiltersDto"
import {
  GetDocumentosComercialesImportadosByFilters,
  GetDocumentosComercialesImportadosByFiltersParamsProps,
  deleteDocumentoComercial,
  postDocumentoComercial,
} from "api/DocumentoImportado.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import axios from "axios"
import DeleteModal from "common/components/Modal/DeleteModal"
import { STATUSCORRECT } from "common/components/ModalVerDocumento/constants"
import { TWENTY_MB } from "common/utils/constants"
import notification from "common/utils/notification"
import LazyOCRTable from "features/OCR/components/Table/OCRTable"
import { OCR_COLUMNS } from "features/OCR/components/Table/OCRTableColumns"
import { useFilterMenu } from "hooks/useFilterMenu"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import "./importar.scss"

const ImportarFacturas = () => {
  const { t } = useTranslation()

  const history = useHistory()
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const [deleteModal, setDeleteModal] = useState<string | undefined>()
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const dtRef = useRef<DataTablePrimeReact<
    GetDocumentosComercialesImportadosByFiltersDtoProps[]
  > | null>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }

  const importarOCRCancelToken = useRef(axios.CancelToken.source())
  const fetchDocumentosComercialesImportados = useCallback(
    () =>
      (queryParams: GetDocumentosComercialesImportadosByFiltersParamsProps) => {
        importarOCRCancelToken.current?.cancel()
        importarOCRCancelToken.current = axios.CancelToken.source()
        return GetDocumentosComercialesImportadosByFilters(
          {
            ...queryParams,
            SkipCount: queryParams?.SkipCount ?? pagination.skipCount,
            MaxResultCount:
              queryParams?.MaxResultCount ?? pagination.itemsPerPage,
            SortingCriteria:
              queryParams?.SortingCriteria ??
              `${sortingCriteria?.property ?? "CreationTime"} ${
                sortingCriteria?.sort ?? "DESC"
              }`,
          },
          importarOCRCancelToken.current.token
        )
      },
    [
      pagination.itemsPerPage,
      pagination.skipCount,
      sortingCriteria.property,
      sortingCriteria.sort,
    ]
  )

  const { items, isLoading, totalCount, fetchTableData } = useFilterMenu({
    serviceCall: fetchDocumentosComercialesImportados,
  })
  const reviewDocument = (id?: string) => {
    history.push(ROUTES.REVISION_FACTURA_OCR(id))
  }
  const handleOnRow = ({
    data,
  }: {
    data: GetDocumentosComercialesImportadosByFiltersDtoProps
  }) => {
    if (!data?.id) return
    reviewDocument(data.id)
  }

  const openDeleteModal = (id?: string) => {
    setDeleteModal(id)
  }
  const handleDelete = async () => {
    if (!deleteModal) return
    try {
      await deleteDocumentoComercial(deleteModal)
      fetchTableData()
      notification.success({
        body: `${t("importar.ocr.msg.deleteCorrect")}`,
      })
      openDeleteModal()
    } catch (error) {
      notification.errorServidor({
        body: `${t("importar.ocr.msg.deleteProblem")}`,
      })
    }
  }

  const handleFileAccepted = async (files: Array<File>) => {
    let notificationInfoId: string | undefined
    try {
      notificationInfoId = notification.info({
        body: t("notification.processing"),
        duration: Infinity,
        icon: faSpinnerThird,
        isIconSpin: true,
      })

      const data = await postDocumentoComercial(files)
      fetchTableData()

      if (data[0]?.status === STATUSCORRECT) {
        notificationInfoId && notification.dismiss(notificationInfoId)
        notification.success({
          body: `${t("importar.ocr.msg.newDocumentRevision")}`,
        })
      }
    } catch (error) {
      notificationInfoId && notification.dismiss(notificationInfoId)
      notification.errorServidor({
        body: `${t("importar.ocr.msg.processProblem")}`,
      })
    }
  }

  return (
    <div className='tw-h-full tw-overflow-hidden tw-flex tw-justify-center'>
      <div className='tw-px-10 tw-pt-2 tw-h-full tw-w-256'>
        <div className='tw-flex tw-justify-between tw-items-center'>
          <p className='tw-text-left'>{t("importar.ocr.title")}</p>
        </div>
        <div className='tw-bg-white [&>div>div]:!tw-min-h-44'>
          <Dropzone
            maxSize={TWENTY_MB}
            maxFiles={Infinity}
            multiple
            accept={{
              "application/pdf": [".pdf"],
            }}
            onFileAccepted={handleFileAccepted}
          />
        </div>
        <h6 className='tw-py-4 tw-text-left'>{t("importar.ocr.subTitle")}</h6>
        <LazyOCRTable
          columns={OCR_COLUMNS(t, reviewDocument, openDeleteModal)}
          dtRef={dtRef}
          items={items}
          loading={isLoading}
          totalCount={totalCount}
          pagination={pagination}
          setPagination={setPagination}
          handleOnRow={handleOnRow}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
        />

        <DeleteModal
          visible={Boolean(deleteModal)}
          onHide={() => openDeleteModal()}
          size='small'
          iconType='remesas'
          buttonLabel={t("nsAssets.modalDelete.confirmLabel")}
          title={t("importar.ocr.deleteTitle")}
          message={t("importar.ocr.deleteQuestion")}
          onConfirm={handleDelete}
        />
      </div>
    </div>
  )
}

export default ImportarFacturas
